import type { ComponentDefinition } from "../types";

export const BATTERY: ComponentDefinition[] = [
  {
    id: "batt-lifepo4-100",
    name: "100 Ah LiFePO4",
    shortName: "LiFePO4 100Ah",
    subtitle: "Leisure battery",
    category: "battery",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    chargePlanningA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
  },
  {
    id: "batt-victron-lfp-100",
    name: "Victron LiFePO4 Smart 100Ah",
    shortName: "LiFePO4 Smart 100Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Victron",
    model: "LiFePO4 Smart 12.8/100",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    // Victron dealers quote 50 A (0.5C) recommended charge for the 12.8/100.
    recommendedChargeA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-victron-lfp-100.webp",
  },
  {
    id: "batt-victron-agm-110",
    name: "Victron AGM Deep Cycle 12V/110Ah",
    shortName: "AGM 110Ah",
    subtitle: "AGM house / leisure battery",
    category: "battery",
    manufacturer: "Victron",
    model: "AGM Deep Cycle 12/110 (BAT412101084)",
    // Victron GEL/AGM datasheet: 110 Ah C20, preferred charge 0.2C = 22 A. No continuous
    // discharge is published, so maxDischargeA is omitted. Fault current is also
    // undeclared and remains unknown; chemistry and Ah do not establish it.
    capacityAh: 110,
    voltage: 12,
    chemistry: "agm",
    recommendedChargeA: 22,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-victron-agm-110.webp",
  },
  {
    id: "batt-victron-lfp-200",
    name: "Victron LiFePO4 Smart 200Ah",
    shortName: "LiFePO4 Smart 200Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Victron",
    model: "LiFePO4 Smart 12.8/200",
    capacityAh: 200,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 400,
    // Dealers quote 100 A (0.5C) recommended charge for the 12.8/200.
    recommendedChargeA: 100,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
    image: "batt-victron-lfp-200.webp",
  },
  {
    id: "batt-ssb-fusion-260",
    name: "SSB Fusion LFPC12-260 260Ah",
    shortName: "SSB Fusion 260",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "SSB",
    model: "LFPC12-260",
    capacityAh: 260,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    recommendedChargeA: 130,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
  },
  {
    id: "batt-haicen-hc12v125",
    name: "Haicen Lithium Pro III 125Ah",
    shortName: "Haicen 125Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Haicen",
    model: "HC12V125",
    capacityAh: 125,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    maxChargeA: 100,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 100,
      },
    ],
  },
  {
    id: "batt-victron-lfp-330",
    name: "Victron LiFePO4 Smart 330Ah",
    shortName: "LiFePO4 Smart 330Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Victron",
    model: "LiFePO4 Smart 12.8/330",
    capacityAh: 330,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 400,
    // 150 A recommended continuous charge per PKYS (Victron dealer); 400 A absolute max.
    recommendedChargeA: 150,
    width: 200,
    height: 80,
    terminals: [
      {
        // 400 A max continuous discharge, the majority-published figure.
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
    image: "batt-victron-lfp-330.webp",
  },
  {
    id: "batt-battleborn-100",
    name: "Battle Born 100Ah",
    shortName: "100Ah LFP",
    subtitle: "Marine / RV / campervan LiFePO4 house battery",
    category: "battery",
    manufacturer: "Battle Born",
    model: "BB10012",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    maxChargeA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-battleborn-100.webp",
  },
  {
    id: "batt-battleborn-gc3",
    name: "Battle Born GC3 270Ah",
    shortName: "GC3 270Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Battle Born",
    model: "BBGC3",
    capacityAh: 270,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 300,
    maxChargeA: 135,
    width: 200,
    height: 80,
    terminals: [
      {
        // 300 A continuous discharge per battlebornbatteries.com.
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    image: "batt-battleborn-gc3.webp",
  },
  {
    id: "batt-renogy-lfp-100",
    name: "Renogy 100Ah LiFePO4 Mini Core",
    shortName: "Mini Core 100Ah",
    subtitle: "Marine / RV LiFePO4 house battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT12100LFP-M",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    chargePlanningA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-renogy-mini-core-100-2026-10-16.jpg",
  },
  {
    id: "batt-renogy-lfp-200",
    name: "Renogy 200Ah LiFePO4 Mini Core",
    shortName: "Mini Core 200Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT12200LFP-M",
    capacityAh: 200,
    voltage: 12.8,
    chemistry: "lifepo4",
    // Renogy Mini Core manual A0, 2026-03-31, printed p55: 200 A continuous discharge.
    maxDischargeA: 200,
    maxChargeA: 100,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    image: "batt-renogy-lfp-200.webp",
  },
  {
    id: "batt-dakota-100",
    name: "Dakota Lithium 100Ah",
    shortName: "100Ah LFP",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Dakota Lithium",
    // DL+ has no 100 Ah model; these are the plain DL's specs (dakotalithium.com).
    model: "12V 100Ah Deep Cycle",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    maxChargeA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-dakota-100.webp",
  },
  {
    id: "batt-epoch-300",
    name: "Epoch Essentials 300Ah",
    shortName: "Essentials 300Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Epoch",
    model: "12300A-H",
    capacityAh: 300,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    maxChargeA: 200,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 250,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 250,
      },
    ],
    image: "batt-epoch-300.webp",
  },
  {
    id: "batt-epoch-460",
    name: "Epoch Essentials 460Ah",
    shortName: "Essentials 460Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Epoch",
    model: "12460A-H",
    capacityAh: 460,
    voltage: 12.8,
    chemistry: "lifepo4",
    // Epoch 12460A-H V02 specification: 200 A continuous in either direction.
    // The separate 400 A / 10 s peak is not continuous capacity.
    maxDischargeA: 200,
    maxChargeA: 200,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-epoch-460.png",
  },
  {
    id: "batt-epoch-460-v2-t",
    name: "Epoch 460Ah V2-T Class T",
    shortName: "Epoch 460Ah V2-T",
    subtitle: "LiFePO4 battery · built-in Class T",
    category: "battery",
    manufacturer: "Epoch",
    model: "C12460A-A",
    // Exact-model V03 datasheet/manual (October 2026), not older V2/Essential ratings.
    // The 400 A internal fuse and 500 A / 10 s pulse are not continuous current limits.
    capacityAh: 460,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 230,
    maxChargeA: 210,
    width: 220,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 230,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 230,
      },
    ],
    image: "batt-epoch-460-v2-t.webp",
  },
  {
    id: "batt-lion-ut1300",
    name: "Lion Energy UT1300",
    shortName: "UT1300 105Ah",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Lion Energy",
    model: "UT1300",
    capacityAh: 105,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 150,
    // Retained planning allowance; original UT1300 manual p4 gives a 100 A maximum.
    chargePlanningA: 80,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-lion-ut1300.webp",
  },
  {
    id: "batt-trojan-t105",
    name: "Trojan T-105 6V GC2",
    shortName: "T-105 GC2",
    subtitle: "Marine / RV flooded house battery (6V)",
    category: "battery",
    manufacturer: "Trojan",
    model: "T-105",
    capacityAh: 225,
    voltage: 6,
    chemistry: "lead-acid",
    maxDischargeA: 75,
    // Trojan User's Guide: bulk charge 10–13 % of C20 → 22.5–29.3 A at 225 Ah.
    recommendedChargeA: 29,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    image: "batt-trojan-t105.webp",
  },
  {
    id: "batt-lifeline-gpl31t",
    name: "Lifeline GPL-31T 105Ah AGM",
    shortName: "GPL-31T 105Ah AGM",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Lifeline",
    model: "GPL-31T",
    capacityAh: 105,
    voltage: 12,
    chemistry: "agm",
    // Legacy planning allowance; not a manufacturer maximum (manual Rev G p20).
    chargePlanningA: 26,
    coldCrankingAmps: 600,
    marineCrankingAmps: 750,
    chargingNotes: "The planning allowance is not a maximum. Lifeline permits high-current bulk charging with its temperature-compensated AGM voltage profile; alternator rating above this allowance does not establish a conflict. See Lifeline Technical Manual Rev G, section 5.4. Starting ratings: exact GPL-31T manufacturer product page. Continuous discharge maximum is unpublished.",
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-lifeline-gpl31t.webp",
  },
  {
    id: "batt-lifeline-gpl-l16t",
    name: "Lifeline GPL-L16T 6V 400Ah AGM",
    shortName: "GPL-L16T 400Ah AGM",
    subtitle: "Leisure battery (6V)",
    category: "battery",
    manufacturer: "Lifeline",
    model: "GPL-L16T",
    capacityAh: 400,
    voltage: 6,
    chemistry: "agm",
    maxDischargeA: 400,
    chargePlanningA: 80,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
    image: "batt-lifeline-gpl-l16t.webp",
  },
  {
    id: "batt-optima-yellow-d34",
    name: "Optima YellowTop D34",
    shortName: "YellowTop D34",
    subtitle: "Dual-purpose battery",
    category: "battery",
    manufacturer: "Optima",
    model: "YellowTop D34",
    capacityAh: 55,
    voltage: 12,
    chemistry: "agm",
    // OPTIMA support distinguishes the battery-charger method from alternator charging.
    chargerMethodChargeA: 10,
    coldCrankingAmps: 750,
    chargingNotes: "The charger-method current applies only to OPTIMA's battery-charger procedure. OPTIMA specifies no amperage limit for voltage-regulated alternator charging within its published voltage range. Verify that voltage range and installed regulation; this does not mean every charging source is compatible. See optimabatteries.com/support. Continuous discharge maximum is unpublished.",
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 750,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 750,
      },
    ],
    image: "batt-optima-yellow-d34.webp",
  },
  {
    id: "batt-agm-100",
    name: "100 Ah AGM",
    shortName: "AGM 100",
    subtitle: "Leisure battery",
    category: "battery",
    capacityAh: 100,
    voltage: 12,
    chemistry: "agm",
    maxDischargeA: 30,
    chargePlanningA: 25,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "batt-start-80",
    name: "80 Ah Start Battery",
    shortName: "Start 80",
    subtitle: "Starter battery",
    category: "battery",
    capacityAh: 80,
    voltage: 12,
    chemistry: "lead-acid",
    maxDischargeA: 600,
    chargePlanningA: 20,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 600,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 600,
      },
    ],
  },
  {
    id: "batt-ssb-ls86c",
    name: "SSB LS86C 120Ah Lithium Start",
    shortName: "SSB LS86C 120",
    subtitle: "Lithium starter battery",
    category: "battery",
    manufacturer: "SSB",
    model: "LS86C",
    capacityAh: 120,
    voltage: 12.8,
    chemistry: "lifepo4",
    // SSB LS86C Issue 4/2024, p1: 2000A is CCA for 15s; 360A is limited to 20min.
    // No unrestricted continuous rating is published; leave maxDischargeA unset.
    // Inverter headroom consequently uses the generic 1C planning fallback (120A).
    maxChargeA: 100,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 1000,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 1000,
      },
    ],
  },
  {
    id: "batt-renogy-smart-100",
    name: "Renogy 100Ah Smart LiFePO4",
    shortName: "Smart 100Ah LFP",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT100LFP12S",
    capacityAh: 100,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 100,
    maxChargeA: 50,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-renogy-smart-100.webp",
  },
  {
    id: "batt-renogy-pro-200",
    name: "Renogy 200Ah Pro Self-Heating LiFePO4",
    shortName: "Pro 200Ah LFP",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT12200LFP-BT",
    capacityAh: 200,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    recommendedChargeA: 100,
    width: 220,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    image: "batt-renogy-pro-200.webp",
  },
  {
    id: "batt-renogy-300",
    name: "Renogy Core 300Ah Self-Heating LiFePO4",
    shortName: "300Ah LFP",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT12300LFPSH",
    capacityAh: 300,
    voltage: 12.8,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    recommendedChargeA: 150,
    width: 230,
    height: 95,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 350,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 350,
      },
    ],
    image: "batt-renogy-300.webp",
  },
  {
    id: "batt-renogy-400",
    name: "Renogy REGO 400Ah LiFePO4",
    shortName: "400Ah LFP",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RBT12400LFPL-SHBT",
    capacityAh: 400,
    voltage: 12.8,
    chemistry: "lifepo4",
    // REGO 12V 400Ah manual A0, p54: 350A continuous discharge, Anderson 350.
    maxDischargeA: 350,
    // Conservative planning value; 300A maximum applies only at 15–50°C.
    chargePlanningA: 200,
    width: 240,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 350,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 350,
      },
    ],
    image: "batt-renogy-400.webp",
  },
  {
    id: "batt-renogy-agm-100",
    name: "Renogy 100Ah AGM Deep Cycle",
    shortName: "100Ah AGM",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RNG-BATT-AGM12-100",
    capacityAh: 100,
    voltage: 12,
    chemistry: "agm",
    // Renogy AGM manual A0, 2025-08-15, printed pp9–10: discharge 100 A; charge 30 A.
    maxDischargeA: 100,
    maxChargeA: 30,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
    ],
    image: "batt-renogy-agm-100.webp",
  },
  {
    id: "batt-renogy-agm-200",
    name: "Renogy 200Ah AGM Deep Cycle",
    shortName: "200Ah AGM",
    subtitle: "Leisure battery",
    category: "battery",
    manufacturer: "Renogy",
    model: "RNG-BATT-AGM12-200",
    capacityAh: 200,
    voltage: 12,
    chemistry: "agm",
    // Renogy AGM manual A0, 2025-08-15, printed pp9–10: discharge 200 A; charge 60 A.
    maxDischargeA: 200,
    maxChargeA: 60,
    width: 220,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
    ],
    image: "batt-renogy-agm-200.webp",
  },
  {
    id: "batt-pylontech-us5000",
    name: "Pylontech US5000",
    shortName: "Pylontech US5000",
    subtitle: "48 V LFP 100 Ah",
    category: "battery",
    manufacturer: "Pylontech",
    model: "US5000",
    capacityAh: 100,
    voltage: 48,
    chemistry: "lifepo4",
    maxDischargeA: 80,
    recommendedChargeA: 80,
    width: 220,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 100,
      },
    ],
    image: "batt-pylontech-us5000.webp",
  },
  {
    id: "batt-victron-lfp-ng-200",
    name: "Victron Lithium NG 25.6V 200Ah",
    shortName: "Lithium NG 25.6/200",
    subtitle: "Leisure battery 24V",
    category: "battery",
    manufacturer: "Victron",
    model: "Lithium NG 25.6V/200Ah",
    capacityAh: 200,
    voltage: 25.6,
    chemistry: "lifepo4",
    maxDischargeA: 200,
    maxChargeA: 200,
    width: 200,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
    image: "batt-victron-lfp-ng-200.webp",
  },
  {
    id: "batt-start-125",
    name: "125 Ah Start Battery",
    shortName: "Start 125",
    subtitle: "Starter battery",
    category: "battery",
    capacityAh: 125,
    voltage: 12,
    chemistry: "lead-acid",
    maxDischargeA: 800,
    chargePlanningA: 30,
    width: 200,
    height: 85,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 800,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 800,
      },
    ],
  },
  // Manufacturer evidence and model boundaries: docs/research/component-manuals-common-50-2026-10-01.md.
  {
    "id": "batt-litime-tm-bluetooth-100",
    "name": "LiTime 12V 100Ah TM Bluetooth LiFePO4",
    "shortName": "TM Bluetooth 100Ah",
    "subtitle": "Trolling / house battery",
    "category": "battery",
    "manufacturer": "LiTime",
    "model": "12.8V 100Ah Trolling Motor Smart (100A BMS)",
    "voltage": 12.8,
    "capacityAh": 100,
    "chemistry": "lifepo4",
    "maxChargeA": 100,
    "maxDischargeA": 100,
    "width": 210,
    "height": 80,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 100,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 100,
        "side": "right",
        "offset": 0.5
      }
    ]
  },
  {
    "id": "batt-fogstar-drift-seatbase-230",
    "name": "Fogstar Drift Gen2 Seat Base 12V 230Ah",
    "shortName": "Drift Seat Base 230Ah",
    "subtitle": "Campervan heated under-seat house battery",
    "category": "battery",
    "manufacturer": "Fogstar",
    "model": "Drift Gen2 Seat Base 12V 230Ah",
    "voltage": 12.8,
    "capacityAh": 230,
    "chemistry": "lifepo4",
    "maxChargeA": 250,
    "maxDischargeA": 250,
    "width": 215,
    "height": 80,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 250,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 250,
        "side": "right",
        "offset": 0.5
      }
    ]
  },
  // Additional manufacturer-reviewed models; evidence: docs/research/component-manuals-additional-50-2026-10-01.md
  {
    id: "batt-sok-sk12v314ph",
    image: "batt-sok-sk12v314ph.webp",
    name: "SOK SK12V314PH Heated LiFePO4 314 Ah",
    shortName: "SK12V314PH 314 Ah",
    subtitle: "12.8 V heated battery with Bluetooth and CAN",
    category: "battery",
    manufacturer: "SOK",
    model: "SK12V314PH",
    width: 220,
    height: 150,
    voltage: 12.8,
    capacityAh: 314,
    chemistry: "lifepo4",
    compatibleVoltages: [12],
    // V1.2 §2.2: continuous charge/discharge <=200 A; 800 A for 10 s.
    // https://europe.sokbattery.com/cdn/shop/t/32/assets/sok-sk12v314ph-user-manual.pdf
    maxChargeA: 200,
    maxDischargeA: 200,
    ampSurge: 800,
    terminals: [
      { id: "pos", label: "BATTERY +", polarity: "positive", direction: "bidi", maxAmp: 200, side: "left", offset: 0.25 },
      { id: "neg", label: "BATTERY −", polarity: "negative", direction: "bidi", maxAmp: 200, side: "left", offset: 0.75 },
    ],
  },
  {
    "id": "batt-sok-sk12v206h",
    "image": "batt-sok-sk12v206h.webp",
    "name": "SOK SK12V206H Heated LiFePO4 206 Ah (150 A version)",
    "shortName": "SK12V206H (150 A version)",
    "subtitle": "Heated metal-case 12.8 V / 206 Ah battery",
    "category": "battery",
    "manufacturer": "SOK",
    "model": "SK12V206H (150 A version)",
    "terminals": [
      {
        "id": "pos",
        "label": "BATTERY +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 150,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "neg",
        "label": "BATTERY −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 150,
        "side": "left",
        "offset": 0.65
      }
    ],
    "width": 180,
    "height": 130,
    "capacityAh": 206,
    "voltage": 12.8,
    "chemistry": "lifepo4",
    "maxDischargeA": 150,
    "maxChargeA": 70,
    "ampSurge": 170,
    "compatibleVoltages": [
      12
    ]
  },
];
